import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// UI
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/components/ui/use-toast";
import AlertModal from "@/components/AlertModal";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";

// Icons
import { ArrowLeft, EyeOff, Eye } from "lucide-react";

// Services
import { deleteNew } from "@/services/news";

// Utils
import formatDate from "@/utils/formatDate";

// Types
import type { News } from "@/modules/news/types";

export const Route = createFileRoute("/_layout-private/news/$id")({
	component: NewsDetail,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			data: search.data as string | undefined,
		};
	},
});

function NewsDetail() {
	const { data } = Route.useSearch();
	const navigate = useNavigate();
	const { toast } = useToast();
	const queryClient = useQueryClient();

	const [parsedData, setParsedData] = useState({} as News);

	useEffect(() => {
		if (data) {
			setParsedData(JSON.parse(data));
		}
	}, [data]);

	// Functions
	const deleteNewMutation = useMutation({
		mutationFn: () => deleteNew(parsedData.id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["news"] });
			toast({
				title: "ສຳເລັດ",
				description: "ລຶບຂ່າວສານສໍາເລັດແລ້ວ",
			});
			navigate({ to: "/news", replace: true });
		},
		onError: () => {
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ຂໍອະໄພ, ມີບາງຢ່າງຜິດພາດ",
				variant: "destructive",
			});
		},
	});

	return (
		<>
			<Breadcrumb>
				<BreadcrumbList>
					<BreadcrumbItem>
						<BreadcrumbLink>
							<Link replace to="/news">
								ຈັດການຂ່າວສານ
							</Link>
						</BreadcrumbLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />

					<BreadcrumbItem>
						<BreadcrumbPage>ລາຍລະອຽດຂ່າວສານ</BreadcrumbPage>
					</BreadcrumbItem>
				</BreadcrumbList>
			</Breadcrumb>
			<section className="mt-[32px]">
				<h2 className="text-2xl font-semibold">ລາຍລະອຽດຂ່າວສານ</h2>
				<p className="text-muted-foreground">ຂໍ້ມູນຂ່າວສານ</p>
				<Separator className="my-6" />
				<button
					type="button"
					onClick={() =>
						navigate({
							to: "/news",
							replace: true,
						})
					}
					className="flex items-center gap-[4px]"
				>
					<ArrowLeft className="w-5 h-5" />
					ກັບຄືນ
				</button>
			</section>
			<section className="max-w-[628px] mx-auto relative space-y-6">
				<div className="space-y-[24px]">
					<div>
						<h3 className="text-[18px] font-semibold">ຂໍ້ມູນຂ່າວສານ</h3>
						<p className="text-muted-foreground text-[14px]">
							ລາຍລະອຽດຂໍ້ມູນຂ່າວສານ
						</p>
						<Separator className="my-6" />
					</div>

					<div className="space-y-[24px] ">
						<div className="flex justify-end">
							<Button
								onClick={() =>
									navigate({
										to: "/news/mutate",
										search: {
											id: parsedData.id,
											data: JSON.stringify(parsedData),
										},
									})
								}
								className=" h-[40px] w-[128px]"
							>
								ແກ້ໄຂ
							</Button>
						</div>
						<img
							alt={parsedData?.title}
							src={
								parsedData?.background_url ||
								"https://via.placeholder.com/628x300"
							}
							className="w-full h-[300px] object-cover"
						/>

						<div className="space-y-[12px] text-[14px]">
							<div className="">
								<Label>ຫົວຂໍ້ຂ່າວ</Label>
								<Input value={parsedData?.title} disabled readOnly />
							</div>
							<div className="">
								<Label>ປະເພດຂ່າວ</Label>
								<Input value={parsedData?.info_type_id} disabled readOnly />
							</div>
						</div>
					</div>

					<div>
						<h3 className="text-[18px] font-semibold">ຂໍ້ມູນການອັບເດດຂໍ້ມູນຂ່າວສານ</h3>
						<p className="text-muted-foreground text-[14px]">
							ລາຍລະອຽດຂໍ້ມູນທີ່ບັນທຶກການເຄື່ອນໄຫວຂອງການປ່ຽນແປງຂໍ້ມູນຂອງຂໍ້ມູນຂ່າວສານນີ້
						</p>
						<Separator className="my-6" />

						<div className="space-y-[12px] text-[14px]">
							<div className="flex items-center ">
								<p className="w-[40%]">ວັນທີສ້າງ:</p>
								<span className="flex-1 font-bold"></span>
							</div>
							<div className="flex items-center ">
								<p className="w-[40%]">ສ້າງໂດຍ:</p>
								<span className="flex-1 font-bold"></span>
							</div>
							<div className="flex items-center ">
								<p className="w-[40%]">ອັບເດດລ່າສຸດ:</p>
								<span className="flex-1 font-bold"></span>
							</div>
							<div className="flex items-center ">
								<p className="w-[40%]">ອັບເດດໂດຍ:</p>
								<span className="flex-1 font-bold"></span>
							</div>
						</div>
						<p className="text-[14px] mt-[32px]">ລຶບຂ່າວສານ</p>
						<div className="flex items-center justify-between text-[14px]">
							<p>
								ຖ້າທ່ານຕ້ອງການລຶບຂ່າວສານສະບັບນີ້ສາມາດກົດ “ລຶບຂ່າວສານ” ປຸ່ມຂວາມືໄດ້ເລີຍ ລຶບຂ່າວສານ
							</p>
							<AlertModal
								title="ທ່ານຕ້ອງການລຶບບັນຊີແທ້ບໍ?"
								desc={
									<p className="text-[14px] text-muted-foreground">
										ຖ້າທ່ານກົດ <span className="text-foreground">“ຢືນຢັນການລຶບ”</span>{" "}
										ລະບົບຈະເຮັດການ ລຶບຂ່າວສານນີ້ທັນທີເຮັດໃຫ້ຂ່າວສານສະບັບນີ້ບໍ່ສາມາດນຳໃຊ້ໄດ້ອີກ.
									</p>
								}
								triggerComponent={
									<Button
										variant="outline"
										disabled={deleteNewMutation.isPending}
									>
										{deleteNewMutation.isPending ? "ກຳລັງລຶບຂ່າວສານ..." : "ລຶບບັນຊີ"}
									</Button>
								}
								confirmFunction={() => {
									deleteNewMutation.mutate();
								}}
							/>
						</div>
					</div>
				</div>
			</section>
		</>
	);
}
